import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from 'src/app/core/models/api-response';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { getTenantId } from 'src/app/services/store-snapshot.service';
import { environment } from 'src/environments/environment';
import {
  IDepartmentPauseCancelRequest,
  IDepartmentPauseCreateAvailability,
  IDepartmentPauseFinishRequest,
  IDepartmentPauseLogApiGroup,
  IDepartmentPausePeriodRequest,
  IDepartmentPauseSettings,
} from '../models/department-pause.model';

const PAUSE_LOG_COLUMN_TO_KEY_MAP: Record<string, string> = {
  startAt: 'StartAt',
  endAt: 'EndAt',
  performedBy: 'PerformedBy',
  performedAt: 'PerformedAt',
  comment: 'Comment',
  state: 'State',
  reason: 'Reason',
};

@Injectable({
  providedIn: 'root',
})
export class DepartmentPauseApiService {
  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
    params: new HttpParams(),
  };

  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/departments`;
  }

  constructor(private http: HttpClient) {}

  getCreateAvailability(departmentId: string): Observable<IDepartmentPauseCreateAvailability> {
    const requestUrl = `${this.url}/${departmentId}/Pause/CreateAvailability`;
    return this.http.get<IDepartmentPauseCreateAvailability>(requestUrl, this.httpOptions);
  }

  getPauseSettings(departmentId: string): Observable<IDepartmentPauseSettings> {
    const requestUrl = `${this.url}/${departmentId}/Pause/Settings`;
    return this.http.get<IDepartmentPauseSettings>(requestUrl, this.httpOptions);
  }

  updatePauseSettings(departmentId: string, settings: IDepartmentPauseSettings): Observable<void> {
    const requestUrl = `${this.url}/${departmentId}/Pause/Settings`;
    return this.http.put<void>(requestUrl, settings, this.httpOptions);
  }

  getPauseLog(
    departmentId: string,
    currentFilter?: MainTableFilterModel
  ): Observable<ApiResponse<IDepartmentPauseLogApiGroup[]>> {
    let params = createFilterParams(currentFilter, undefined, PAUSE_LOG_COLUMN_TO_KEY_MAP);
    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      currentFilter,
      PAUSE_LOG_COLUMN_TO_KEY_MAP
    );
    const requestUrl = `${this.url}/${departmentId}/Pause/Log`;
    return this.http.get<ApiResponse<IDepartmentPauseLogApiGroup[]>>(requestUrl, {
      ...this.httpOptions,
      params,
    });
  }

  updatePause(
    departmentId: string,
    pauseId: string,
    { startAt, endAt, comment, modules }: IDepartmentPausePeriodRequest
  ): Observable<void> {
    const requestUrl = `${this.url}/${departmentId}/Pause/${pauseId}`;
    return this.http.patch<void>(
      requestUrl,
      {
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
        comment,
        ...(modules?.length ? { modules } : {}),
      },
      this.httpOptions
    );
  }

  createPausePeriod(
    departmentId: string,
    { startAt, endAt, comment, modules }: IDepartmentPausePeriodRequest
  ): Observable<void> {
    const requestUrl = `${this.url}/${departmentId}/Pause`;
    return this.http.post<void>(
      requestUrl,
      {
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
        comment,
        ...(modules?.length ? { modules } : {}),
      },
      this.httpOptions
    );
  }

  finishPause(
    departmentId: string,
    pauseId: string,
    { endAt }: IDepartmentPauseFinishRequest
  ): Observable<void> {
    const requestUrl = `${this.url}/${departmentId}/Pause/${pauseId}/Finish`;
    return this.http.post<void>(
      requestUrl,
      {
        endAt: endAt.toISOString(),
      },
      this.httpOptions
    );
  }

  cancelPause(
    departmentId: string,
    pauseId: string,
    { comment }: IDepartmentPauseCancelRequest
  ): Observable<void> {
    const requestUrl = `${this.url}/${departmentId}/Pause/${pauseId}/Cancel`;
    return this.http.post<void>(requestUrl, { comment }, this.httpOptions);
  }
}
